/*
 * Product lookup component
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2025 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import { ProductService } from 'app/shared/services';
import { take } from 'rxjs/operators';
import { Product } from 'app/shared/models';

@Component ( {
    selector: 'reporter-product-lookup',
    templateUrl: './product-lookup.component.html',
    styleUrls: ['./product-lookup.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ProductLookupComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ProductLookupComponent),
            multi: true
        },
    ],
    standalone: false
} )
export class ProductLookupComponent implements AfterViewInit {

  @Input () incidentId: number;
  @Input () linkedProduct : boolean | null = null;
  @Output () productChange = new EventEmitter<Product> ();

  products: Product[];
  selected: Product;
  innerValue: string;

  constructor( private renderer: Renderer2,
               private elementRef: ElementRef,
               private productService: ProductService ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return this.innerValue;
  }

  get required(): boolean {
    return Object.prototype.hasOwnProperty.call(this.elementRef.nativeElement.attributes, 'required' );
  }

  initialized = false;
  showError = false;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit() {
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;

    if ( this.value && this.linkedProduct && !this.selected ) {
      this.selected = { number: this.value };
    }
    this.showError = false;
  }

  writeValue( value: any ): void {
    this.innerValue = value;
    this.onChange ( value );
    this.onTouched ();
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( ( control.value === null || control.value === undefined ) && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    return null;
  }

  search() {
    this.showError = false;
    this.productService.search ( this.incidentId, this.innerValue )
      .pipe ( take ( 1 ) )
      .subscribe ( result => {
        this.products = result;
        if ( this.products.length === 1 ) {
          this.select ( this.products[ 0 ] );
        }
      }, error => {
        if(error.status === 404){
          this.showError = true;
        }
      } );
  }

  clear() {
    this.value = undefined;
    this.selected = undefined;
    this.products = undefined;
    this.innerValue = undefined;
    this.linkedProduct = false;
  }

  select( product: Product ) {
    this.writeValue ( product.number );
    this.selected = product;
    this.linkedProduct = true;
    this.productChange.emit ( product );
  }

  get isLinked(): boolean{
    return !!this.linkedProduct || !!this.selected?.number;
  }
}
